import axios from 'smg/axios';
import { Start, Stop } from 'smg/buttons';
import { ShowError } from 'smg/errors';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import * as uploadClaims from 'smg/upload-claims';
import { Uploader } from '../../components/uploader';
import * as store from '../../free-quote/store';
import { Relref } from '../../hugo';

declare interface myWindow extends Window {
    dataLayer: Record<string, unknown>[];
    readonly product: string;
}
declare const window: myWindow;

main();

function main() {
    store.Init();

    const settings = store.Get();
    if (!settings.getStarted || !settings.details) {
        store.Redirect();
        return;
    }

    const $form = document.querySelector('#free-quote-form') as HTMLFormElement;
    const $fieldset = $form.querySelector('fieldset') as HTMLFieldSetElement;
    const $submit = document.querySelector('#free-quote-submit') as HTMLButtonElement;
    const $submitText = document.querySelector('#free-quote-submit-text') as HTMLButtonElement;

    const $frontUpload = document.querySelector('#free-quote-upload-front') as HTMLInputElement;
    const $frontNotes = document.querySelector('#free-quote-notes-front') as HTMLTextAreaElement;

    const $backUpload = document.querySelector<HTMLInputElement>('#free-quote-upload-back');
    const $backNotes = document.querySelector<HTMLTextAreaElement>('#free-quote-notes-back');

    const hasInput = () =>
        frontUploader.getUploads().length > 0 ||
        $frontNotes.value.trim().length > 0 ||
        (backUploader && backUploader.getUploads().length > 0) ||
        ($backNotes && $backNotes?.value.trim().length > 0);

    const updateSubmit = (hasUploadIDs = false) => {
        if (hasInput() || hasUploadIDs) {
            $submitText.textContent = 'Continue';
        } else {
            $submitText.textContent = 'Skip';
        }
    };

    let frontUploadIDs: number[] = [];
    let backUploadIDs: number[] = [];
    if (settings.upload) {
        if (settings.upload.front) {
            const front = settings.upload.front;

            frontUploadIDs = front.uploadIDs;
            uploadClaims.RegisterRecord(front.uploadClaims);

            $frontNotes.value = front.notes;
        }

        if (settings.upload.back) {
            const back = settings.upload.back;

            backUploadIDs = back.uploadIDs;
            uploadClaims.RegisterRecord(back.uploadClaims);

            if ($backNotes) {
                $backNotes.value = back.notes;
            }
        }
    }

    const frontUploader = new Uploader($frontUpload.closest('.uploader-dropzone') as HTMLLabelElement, {
        onidschange: () => {
            updateSubmit();
        },
        uploadIDs: frontUploadIDs,
        tags: () => ['front'],
    });

    let backUploader: Uploader | undefined;
    if ($backUpload) {
        backUploader = new Uploader($backUpload.closest('.uploader-dropzone') as HTMLLabelElement, {
            onidschange: () => {
                updateSubmit();
            },
            uploadIDs: backUploadIDs,
            tags: () => ['back'],
        });
    }

    $frontNotes.addEventListener('input', () => {
        updateSubmit();
    });

    $backNotes?.addEventListener('input', () => {
        updateSubmit();
    });

    updateSubmit(frontUploadIDs.length !== 0 || backUploadIDs.length !== 0);

    $submit.addEventListener('click', async () => {
        Start($submit);
        $fieldset.disabled = true;

        if (hasInput()) {
            // Awaits live inside the try so a PUT-failure throw hits
            // catch/finally (re-enables form) instead of stranding it.
            let frontUploadIDs: number[];
            let backUploadIDs: number[];
            try {
                frontUploadIDs = await frontUploader.getUploadIDs();
                backUploadIDs = await backUploader?.getUploadIDs() ?? [];

                if (frontUploadIDs.length) {
                    await axios.patch('/v2/uploads/keep', {
                        'uploadIDs': frontUploadIDs,
                    });
                }

                if (backUploadIDs.length) {
                    await axios.patch('/v2/uploads/keep', {
                        'uploadIDs': backUploadIDs,
                    });
                }
            } catch (e) {
                ShowError(e);
                throw e;
            } finally {
                Stop($submit);
                $fieldset.disabled = false;
            }

            const settings = store.Get();

            settings.upload = {
                front: {
                    uploadIDs: frontUploadIDs,
                    notes: $frontNotes.value,
                    uploadClaims: uploadClaims.Record(frontUploadIDs),
                },
                back: {
                    uploadIDs: backUploadIDs,
                    notes: $backNotes?.value ?? '',
                    uploadClaims: uploadClaims.Record(backUploadIDs),
                },
            };

            if (settings.inDepthEnabled) {
                store.Set(settings);
                if (window.location.href.includes('american-made')) {
                    window.location.href = Relref('/american-made-free-quote/in-depth');
                } else {
                    window.location.href = Relref('/free-quote/in-depth');
                }
            } else {
                const $body = html.FromString(`
                <div>
                    <div class="d-flex flex-column align-items-center gap-3 flex-md-row mb-4">
                        <div>
                            <label class="text-center shadow-sm bg-white rounded-3" style="cursor:pointer;padding:1.5rem 1.25rem 2rem">
                                <h3>Yes</h3>
                                <p>I know exactly what I want and would like to provide more details</p>

                                <input id="modal-yes-more-details" type="radio" name="specify-more-details" value="yes" class="btn-check yes-more-details">
                                <label for="modal-yes-more-details" class="btn btn-lg btn-outline-primary specify-details-label">Select</label>
                            </label>
                        </div>
                        <div>
                            <label class="text-center shadow-sm bg-white rounded-3" style="cursor:pointer;padding:1.5rem 1.25rem 2rem">
                                <h3>No</h3>
                                <p>I would like artists to choose what options will look best for my idea</p>

                                <input id="modal-no-more-details" type="radio" name="specify-more-details" value="no" class="btn-check no-more-details">
                                <label for="modal-no-more-details" class="btn btn-lg btn-outline-primary specify-details-label">Select</label>
                            </label>
                        </div>
                    </div>
                    <div class="text-end">
                        <button id="modal-continue" type="button" class="btn btn-primary btn-lg fw-bold ms-auto d-block" autocomplete="off" disabled>
                            Continue <i class="bi bi-chevron-right"></i>
                        </button>
                    </div>
                </div>`);

                const $yes = $body.querySelector('#modal-yes-more-details') as HTMLInputElement;
                const $no = $body.querySelector('#modal-no-more-details') as HTMLInputElement;

                $yes.addEventListener('change', () => {
                    $continue.disabled = false;
                });
                $no.addEventListener('change', () => {
                    $continue.disabled = false;
                });

                const $continue = $body.querySelector('#modal-continue') as HTMLButtonElement;
                $continue.addEventListener('click', () => {
                    Start($continue);
                    $yes.disabled = true;
                    $no.disabled = true;

                    if ($yes.checked) {
                        settings.inDepthEnabled = true;
                        store.Set(settings);
                        if (window.location.href.includes('american-made')) {
                            window.location.href = Relref('/american-made-free-quote/in-depth');
                        } else {
                            window.location.href = Relref('/free-quote/in-depth');
                        }
                    } else {
                        store.Set(settings);
                        if (window.location.href.includes('american-made')) {
                            window.location.href = Relref('/american-made-free-quote/review');
                        } else {
                            window.location.href = Relref('/free-quote/review');
                        }
                    }
                });

                new Modal({
                    title: 'Do you want to specify more details?',
                    body: $body,
                    width: '37.5rem',
                    appendClassBySelector: {
                        '.modal-content': 'p-3 pt-0 bg-gray-100',
                        '.modal-header': 'border-bottom-0 pe-0 pe-sm-2 mt-2 align-items-start',
                        '.modal-title': 'text-center fs-2 w-100 p-0 mt-2',
                        '.modal-footer': 'd-none',
                        '.modal-dialog': 'modal-custom-width',
                    },
                });
            }
        } else {
            const settings = store.Get();
            settings.upload = {
                front: {
                    uploadIDs: [],
                    notes: '',
                    uploadClaims: {},
                },
                back: {
                    uploadIDs: [],
                    notes: '',
                    uploadClaims: {},
                },
            };
            store.Set(settings);

            if (window.location.href.includes('american-made')) {
                window.location.href = Relref('/american-made-free-quote/review');
            } else {
                window.location.href = Relref('/free-quote/review');
            }
        }
    });

    const $spinner = document.querySelector('#free-quote-spinner-parent') as HTMLElement;
    const $content = document.querySelector('#free-quote-content') as HTMLElement;
    $spinner.remove();
    $content.classList.remove('d-none');
}
